import {
  createHoloCard,
  type CreateHoloCardOptions,
  type HoloCard,
  type HoloEffect,
} from "@kongyo2/cards-css";
import "@kongyo2/cards-css/styles.css";

/**
 * The foil on a specimen card is a reading of the specimen, not a decoration:
 * its pool comes from what the card measurably is (cell class, real synapse
 * degree, hatch order) and the region steers which foil inside that pool. The
 * neuron id seeds both the pick and the procedural texture, so one neuron keeps
 * one face forever - the same rule the specimen drawing already follows.
 */

export type FoilTier = "common" | "uncommon" | "rare" | "legacy";

export interface SpecimenFoilInput {
  neuronId: number;
  /** annotation region of the neuron's slot: optic_lobe | central_brain | ventral_nerve_cord */
  region?: string;
  /** annotation cell class, e.g. "AN_motor_neuron" or "intrinsic" */
  cellClass?: string;
  /** real synapse degree across the 85 tapes, when the indexer supplies it */
  degree?: number;
  /** 0-based position among all cards ever minted, when known */
  hatchIndex?: number;
}

export interface FoilChoice {
  effect: HoloEffect;
  tier: FoilTier;
  textureSeed: number;
}

const TIER_POOL: Record<FoilTier, readonly HoloEffect[]> = {
  // every card carries a foil: the tier decides which one, never whether one
  // exists, because "no foil" reads as a rendering bug on a museum wall
  common: ["holo", "reverse"],
  uncommon: ["reverse", "radiant", "mosaic"],
  rare: ["aurora", "prism", "crystal", "oilslick"],
  legacy: ["gold", "rainbow", "sunburst"],
};

/** region shifts the pick inside the pool, so two cards of one tier still differ */
function regionOffset(region: string | undefined): number {
  switch (region) {
    case "optic_lobe":
      return 1;
    case "central_brain":
      return 2;
    case "ventral_nerve_cord":
      return 3;
    default:
      return 0;
  }
}

/**
 * The library demo's own configuration per foil, copied from its card array
 * (https://kongyo2.github.io/cards-css/). The demo's beautiful foil is the
 * library at these settings; our cards use the same ones, with `textureSeed`
 * fixed to the neuron so a specimen's face never changes.
 */

/** Only the options the demo passes, never the artwork the caller supplies. */
type FoilSetup = Omit<CreateHoloCardOptions, "image" | "content" | "imageAlt" | "effect">;

const DEMO_FOIL: Partial<Record<HoloEffect, FoilSetup>> = {
  holo: { physics: { maxTilt: 18 }, visual: { saturate: 1.1 }, depth: { strength: 12 } },
  reverse: {
    // the demo measures this foil at a longer parallax and a wider glare travel;
    // its custom glare stops are left out (the lint rule reserves the option's
    // own name), so this foil keeps the library's default glare outline
    physics: { parallax: 1.3, glareRange: 1.1 },
  },
  cosmos: {
    physics: { maxTiltX: 16, maxTiltY: 11, parallax: 1.2 },
    showcase: { intensity: 30 },
    palette: { preset: "aurora" },
  },
  oilslick: { physics: { parallax: 1.4 } },
  sunburst: { glow: "hsl(45, 100%, 80%)" },
  mosaic: { visual: { glitterSize: 32 } },
};

const MOTOR = /motor|efferent/i;

export function foilTier(input: SpecimenFoilInput): FoilTier {
  if (input.hatchIndex !== undefined && input.hatchIndex < 100) return "legacy";

  if (MOTOR.test(input.cellClass ?? "") || (input.degree ?? 0) >= 24) return "rare";

  if ((input.degree ?? 0) >= 8) return "uncommon";

  return "common";
}

export function foilFor(input: SpecimenFoilInput): FoilChoice {
  const tier = foilTier(input);
  const pool = TIER_POOL[tier];
  const offset = regionOffset(input.region);
  const index = (Math.abs(Math.trunc(input.neuronId)) + offset) % pool.length;

  return { effect: pool[index], tier, textureSeed: Math.trunc(input.neuronId) };
}

export interface MountHoloOptions {
  /** alt text: the specimen's Latin name, identical in every language */
  alt: string;
  /** the specimen drawing, from artSvg(seed, size, variant) */
  art: string;
  glow?: string;
  /** the face's width / height; our faces are square */
  aspectRatio?: number;
  /** the label the demo prints in its rarity pill, when the caller has one */
  rarity?: string;
  /**
   * Whether this card may tilt under the pointer. Defaults to `holoIsInteractive()`.
   * A grid of cards sets it false past the first few: the foil still reads, and
   * fifty pointer-reactive cards is not a collection, it is a space heater.
   */
  interactive?: boolean;
}

/**
 * The demo's rarity pill, copied from its own stylesheet: the gradient chip it
 * prints over the top-right corner of each card.
 */
function rarityPill(label: string): HTMLElement {
  const pill = document.createElement("div");

  pill.className = "rarity";
  pill.textContent = label;

  return pill;
}

export interface MountedHolo {
  card: HoloCard;
  destroy: () => void;
}

function artDataUri(art: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(art)}`;
}

/** Foil only where a pointer can hover and motion is welcome; touch gets a flat card. */
export function holoIsInteractive(): boolean {
  const motion = window.matchMedia?.("(prefers-reduced-motion: reduce)");

  if (motion?.matches !== false) return false;

  const pointerFine = window.matchMedia?.("(pointer: fine)");

  return pointerFine?.matches === true;
}

/**
 * Mounts the foil card into `host` and returns the destroy the caller must call
 * when it rebuilds its HTML - the views here re-render by replacing children.
 */
export function mountHoloCard(
  host: HTMLElement,
  specimen: SpecimenFoilInput,
  options: MountHoloOptions,
): MountedHolo {
  const choice = foilFor(specimen);

  const settings: CreateHoloCardOptions = {
    // the demo's own receipt for this foil first, then what the app has to say:
    // the library's demo settings are what a foil card is supposed to look like,
    // and nothing here dims the foil - a subdued foil is not a restrained one,
    // it is a broken one.
    ...DEMO_FOIL[choice.effect],
    image: artDataUri(options.art),
    imageAlt: options.alt,
    effect: choice.effect,
    textureSeed: choice.textureSeed,
    className: "specimen-holo card-face",
    interactive: options.interactive ?? holoIsInteractive(),
    glow: options.glow ?? DEMO_FOIL[choice.effect]?.glow ?? "transparent",
    aspectRatio: options.aspectRatio ?? 1,
    // the demo's own two behaviours: one automatic sweep when the card mounts,
    // and a tap or click that enlarges the card and holds its foil. The library
    // skips the sweep entirely under prefers-reduced-motion.
    showcase: true,
    activateOnClick: true,
  };

  // the demo's rarity pill, in the demo's own style: a gradient chip riding the
  // top-right corner of the face
  if (options.rarity !== undefined) settings.overlay = rarityPill(options.rarity);

  const card = createHoloCard(settings);

  // the seams the browser suite reads: which foil a specimen got, and that it
  // is mounted at all
  card.element.dataset.foil = `${choice.tier}:${choice.effect}`;
  card.element.dataset.holo = options.interactive === false ? "static" : "on";
  host.replaceChildren(card.element);

  return { card, destroy: () => card.destroy() };
}
